import { ANONYMITY_COPY } from "@givehere/contracts";
import { LegalLink, LegalSection, LegalShell, SupportContact } from "./LegalShell.js";
import { PLATFORM_MAILING_ADDRESS, useSite } from "./site.js";

/**
 * Privacy Policy (GH-812). Every claim here is something the code does today; the places the
 * policy is undecided say so in brackets rather than guessing.
 *
 * Sources, so a reviewer can check a sentence against the thing it describes:
 *  - location: `coarsenPosition` (two decimal places, before it leaves the device), SPEC.md §6.2;
 *  - donor note: `donations.donor_note`, read by the console's gift detail behind
 *    `reconciliation.view`, on the log redaction list (GH-793); shown on the event screen only
 *    with the donor's per-gift tick (`donor_note_on_screen`) and a host's approval
 *    (`donation_screen_note_decisions`), never for a donor hidden from organizations, with no name
 *    on an anonymous gift (GH-887; the checkbox is not offered until counsel signs off — `NOTE_ON_SCREEN_OFFERED`);
 *  - anonymity (DEC-049, GH-857/858): "anonymous" is public only — the console names the donor
 *    with an "Anonymous publicly" flag; "Anonymous to all organizations"
 *    (`donor_profiles.hidden_from_organizations`, read through `donation_donor_identities`) is
 *    what removes a name and contact details from every organization surface, CSV and email;
 *  - deletion: `POST /v1/donor/account/delete` and `donors.deleteAccount`;
 *  - retention: DEC-015 (7 years financial, 400 days identity — nothing auto-deletes yet);
 *  - browser storage: `donor-session.ts`, `donor-memory.ts`; fonts are self-hosted.
 */
export function PrivacyPage() {
  const { organizationLegalName: entity, productName: product } = useSite();
  return (
    <LegalShell title="Privacy Policy">
      <LegalSection id="who" title="Who we are">
        <p>
          {product} is operated by {entity}, which decides how the information described here is
          used. This policy covers the {product} website and apps.
        </p>
      </LegalSection>

      <LegalSection id="summary" title="In short">
        <ul>
          <li>You can give without an account, and anonymously to the public.</li>
          <li>
            The organization you give to sees who you are, unless you turn on &ldquo;
            {ANONYMITY_COPY.organizationsLabel}&rdquo;.
          </li>
          <li>We never see your full card number or your bank password.</li>
          <li>We do not sell your information or use it for advertising.</li>
          <li>
            Where you give can suggest your religious beliefs, so we treat giving records as
            sensitive.
          </li>
        </ul>
      </LegalSection>

      <LegalSection id="collect" title="What we collect">
        <ul>
          <li>
            <strong>Your gift:</strong> the amount, the organization and event, any fund or purpose
            you chose, the time, and whether you chose to be named. This is the gift record, and it
            is a financial record.
          </li>
          <li>
            <strong>Payment details:</strong> card, Apple Pay, and Google Pay payments are entered
            into Stripe, and bank payments are linked through Plaid. We receive a reference to the
            payment method and details such as its type and last four digits — never a full card
            number, security code, bank password, or full account number.
          </li>
          <li>
            <strong>Contact details:</strong> a mobile number or email address, when you sign in or
            ask for a receipt. Signing in proves a number or address with a one-time code. If you
            sign in with Apple, we may receive the name you share with Apple once, at first sign-in.
          </li>
          <li>
            <strong>Optional profile details:</strong> a display name and a mailing address, only if
            you add them.
          </li>
          <li>
            <strong>A note with your gift:</strong> up to 280 characters, if you write one. It is
            seen by the people authorized to review the organization&rsquo;s transactions, and it is
            kept out of our logs. It is private unless you tick &ldquo;Show on the event
            screen&rdquo; for that gift. If you do, the organization&rsquo;s event staff review the
            note first, and only once they approve it can it appear with your gift on the
            event&rsquo;s screen and in the event&rsquo;s public list of recent gifts. Your public
            name appears beside it if you chose to be named for that gift; an anonymous gift&rsquo;s
            note appears with no name. If you have turned on &ldquo;
            {ANONYMITY_COPY.organizationsLabel}&rdquo;, your note is never shown. Please do not put
            anything in a note you would not want read aloud in the room, such as health details or
            someone else&rsquo;s private information.
          </li>
          <li>
            <strong>Event activity:</strong> when signed in, your check-ins, reactions, and answers
            to an event&rsquo;s prompts.
          </li>
          <li>
            <strong>Approximate location, only if you ask:</strong> &ldquo;near me&rdquo; rounds
            your position on your device to about a kilometre — well under a mile — before it is
            sent. It is used to sort nearby organizations and events, and it is never stored. Pages
            you open from a code or link never ask for your location.
          </li>
          <li>
            <strong>Technical records:</strong> logs of requests and errors with personal details
            removed, and a keyed fingerprint of your connection used to limit abuse of sign-in
            codes.
          </li>
        </ul>
        <p>
          On the website, your browser keeps your sign-in token and, if you chose to be named, the
          display name you used, so you need not retype it. We use no advertising or analytics
          trackers, and our fonts are served from our own site so no font service learns which page
          you opened.
        </p>
      </LegalSection>

      <LegalSection id="use" title="How we use it">
        <ul>
          <li>To process your gift, issue your receipt, and make grants to organizations.</li>
          <li>To sign you in and keep your receipts and payment methods together.</li>
          <li>To show organizations and events near you, when you ask.</li>
          <li>To run events: check-ins, prompts, and the public goal display.</li>
          <li>
            To prevent fraud and abuse, and to keep financial and tax records the law requires.
          </li>
          <li>To answer you when you contact us.</li>
        </ul>
        <p>
          Receipts and gift updates are not marketing. We send other messages only with your
          consent, and you can stop them at any time.
        </p>
      </LegalSection>

      <LegalSection id="share" title="Who we share it with">
        <p>
          <strong>The organization you give to</strong> sees your gift, any fund or purpose you
          chose, your note, and your name and contact details — including when you gave anonymously
          to the public. Its staff can download that list and email you through {product}; every
          such email lets you stop them, and what an organization downloads is then in its own
          hands. Turning on <strong>{ANONYMITY_COPY.organizationsLabel}</strong> removes your name
          and contact details from what every organization sees.
        </p>
        <p>
          <strong>Service providers</strong> who process information for us under contract:
        </p>
        <ul>
          <li>Stripe — card, Apple Pay, and Google Pay payments.</li>
          <li>Plaid — linking a bank account and bank payments.</li>
          <li>Chariot — donor-advised fund grants through DAFpay, when you choose it.</li>
          <li>Apple and Google — signing in, when you choose them.</li>
          <li>Amazon Web Services (Simple Email Service) — sending email.</li>
          <li>Google Cloud — hosting and our database.</li>
          <li>Twilio — sending text messages, such as sign-in codes.</li>
        </ul>
        <p>
          We also disclose information when the law requires it, to protect people from fraud or
          harm, or as part of a transfer of the platform to a successor that keeps this policy.
        </p>
        <p>
          <strong>We do not sell your personal information</strong>, and we do not share it for
          targeted advertising.
        </p>
      </LegalSection>

      <LegalSection id="sensitive" title="Religious affiliation">
        <p>
          Many organizations on {product} are houses of worship, so where and when you give can
          suggest your religious beliefs. We treat that as sensitive information: we use it only to
          process and record your gifts and to provide the features you choose, never for
          advertising or profiling, and we do not disclose it except as this policy describes.
        </p>
      </LegalSection>

      <LegalSection id="anonymous" title="Anonymous giving">
        <p>
          Every gift is anonymous to the public by default. Event displays, public totals, and other
          donors never see who gave unless you choose to be named. &ldquo;Anonymous&rdquo; means
          anonymous to the public: the organization you give to sees your name and your gift, so it
          can thank you and keep its own records. {entity} keeps the full record, because it is the
          recipient of the gift.
        </p>
        <p>
          If you do not want organizations to know who you are, turn on{" "}
          <strong>{ANONYMITY_COPY.organizationsLabel}</strong> in your profile, under{" "}
          {ANONYMITY_COPY.advancedPrivacyLabel}. Every organization you have given to, and any you
          give to later, then sees &ldquo;Anonymous donor&rdquo; instead of your name and contact
          details. Your gifts still count toward their totals and your receipts still come from{" "}
          {product}, but organizations cannot thank you, invite you, or send you updates. You can
          turn it off in the same place.
        </p>
      </LegalSection>

      <LegalSection id="retention" title="How long we keep it">
        <ul>
          <li>Gift, receipt, and accounting records: 7 years, as financial and tax records.</li>
          <li>
            Contact details, profile, and sign-in sessions: while you use {product}; we intend to
            delete them 400 days after last use. A session expires after 400 days.
          </li>
          <li>Approximate location: not stored.</li>
        </ul>
        <p>Today nothing is deleted automatically; we delete on request.</p>
      </LegalSection>

      <LegalSection id="rights" title="Your choices and rights">
        <ul>
          <li>
            <strong>Delete your account</strong> in the app, or ask us —{" "}
            <LegalLink to="accountDeletion">how to delete your account</LegalLink>.
          </li>
          <li>
            <strong>See, correct, or get a copy</strong> of your information by contacting{" "}
            <SupportContact />.
          </li>
          <li>
            <strong>Stop optional messages</strong> at any time by contacting <SupportContact />.
          </li>
        </ul>
        <p>
          <strong>State privacy rights.</strong> Residents of California and other states with
          privacy laws may have the right to know what we collect and why, to access, correct, and
          delete it, to receive a portable copy, to opt out of sale or sharing (we do neither), to
          limit the use of sensitive information (we use it only as described above), and not to be
          treated differently for using these rights. You or an authorized agent may make a request
          through <SupportContact />; we will confirm it is you before acting, and you may appeal a
          decision by replying to it. We respond within 45 days.
        </p>
      </LegalSection>

      <LegalSection id="security" title="Security">
        <p>
          Information is encrypted in transit, payment credentials stay with Stripe and Plaid, and
          access to donor records is limited to people who need it. No system is perfectly secure;
          if a breach affects you, we will tell you as the law requires.
        </p>
      </LegalSection>

      <LegalSection id="children" title="Children">
        <p>
          {product} is not directed to children under 13, and we do not knowingly collect their
          information. If you believe a child has given us information, contact us and we will
          delete it. You must be 18 or older to give or to hold an account.
        </p>
      </LegalSection>

      <LegalSection id="changes" title="Changes">
        <p>We will post changes here and update the date above.</p>
      </LegalSection>

      <LegalSection id="contact" title="Contact">
        <p>
          {entity}, {PLATFORM_MAILING_ADDRESS}. Privacy questions and requests: <SupportContact />.
        </p>
      </LegalSection>
    </LegalShell>
  );
}
